import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react' // React Grid Logic
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import 'ag-grid-community/styles/ag-grid.css' // Core CSS
import 'ag-grid-community/styles/ag-theme-quartz.css' // Theme
import 'ag-grid-enterprise'
import React, { useMemo } from 'react'
import '../styles/index.scss'

import { Divider, Flex, Typography } from 'antd'
import styled, { css } from 'styled-components'
import { TableProps } from '../table.model'
import TableAddButton from './TableAddButton'
import TableBulkDeleteButton from './TableBulkDeleteButton'
import TableDropdown from './TableColumnDropdown'
import TableDateFilter from './TableDateFilter'
import TableDownloadButton from './TableDownloadButton'
import TableLoadingOverlay from './TableLoadingOverlay'
import TableNoRowOverlay from './TableNoRowOverlay'
import TablePagination from './TablePagination'
import { useTableContext } from './TableProvider'
import TableResetButton from './TableResetButton'
import TableSearch from './TableSearch'
import TableUploadButton from './TableUploadButton'

const WrapperTable: any = styled(AgGridReact<TableProps>)<TableProps>`
  .ag-header-cell-text {
    ${(props) =>
      props.tableHeaderFontColor &&
      css`
        color: ${props.tableHeaderFontColor};
      `}
  }
  .ag-header {
    .ag-pinned-left-header,
    .ag-header-viewport {
      ${(props) =>
        props.tableHeaderBackgroundColor &&
        css`
          background-color: ${props.tableHeaderBackgroundColor};
        `}
    }
  }
  .ag-root-wrapper,
  .ag-root-wrapper-body,
  .ag-body,
  .ag-root.ag-layout-normal {
    ${(props) =>
      props.settingHeightTable &&
      css`
        max-height: calc(${props.settingHeightTable}) !important;
      `}
  }
  .ag-center-cols-container,
  .ag-center-cols-viewport {
    ${(props) =>
      props.rowData?.length === 0 &&
      css`
        min-height: 250px !important;
      `}
  }
`

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY ?? '')

ModuleRegistry.registerModules([ClientSideRowModelModule, ClipboardModule, RangeSelectionModule, RowGroupingModule])

// Create new GridExample component
const TableContent: React.FC = () => {
  const {
    defs,
    columnDefs,
    tableRef,
    tableName,
    tableDescription,
    rowData,
    disableTableControl = false,
    groupDefaultExpanded,
    rootContainerStyle,
    tableContainerStyle,
    pagination,
    paginationPageSizeSelector,
    paginationPageSize,
    accentedSort = true,
    isSearch,
    suppressNoRowsOverlay = true,
    tableSearchData,
    rowSelection = 'single',
    domLayout = 'autoHeight',
    loading,
    onGridReady,
    onSortChanged,
    customFilter,
    onColumnResized,
    ...etc
  } = useTableContext()

  const isGroupingTable = useMemo(() => defs.some((item) => item.rowGroup), [defs])

  return (
    <div style={rootContainerStyle}>
      {tableSearchData && customFilter?.({ ...tableSearchData, loading })}
      <Flex id='search-container' gap={8} justify='space-between' align='center'>
        {tableName && (
          <Typography.Text
            style={{
              fontWeight: 'bold',
              fontSize: 18,
            }}
          >
            {tableName}
          </Typography.Text>
        )}
        {tableDescription && (
          <React.Fragment>
            <Divider
              type='vertical'
              style={{
                height: 2,
              }}
            />
            <Typography.Text
              style={{
                fontWeight: '400',
                fontSize: 14,
                color: '#46515A',
              }}
            >
              {tableDescription}
            </Typography.Text>
          </React.Fragment>
        )}
        <Flex gap={8} align='center' justify='end' flex={1}>
          <TableSearch />
          <TableDropdown />
          <TableDateFilter />
          <TableResetButton />
          {!disableTableControl && <Divider type='vertical' />}
          <TableBulkDeleteButton />
          <TableAddButton />
          <TableUploadButton />
          <TableDownloadButton />
        </Flex>
      </Flex>
      <div
        style={tableContainerStyle}
        className={`ag-theme-quartz ${isGroupingTable ? 'common-grouping-table' : 'common-table'} ${
          rowData && rowData?.length > 0 ? '' : 'no-data'
        }`}
      >
        <WrapperTable
          ref={tableRef}
          rowData={rowData}
          columnDefs={columnDefs ?? defs}
          pagination={pagination}
          groupDefaultExpanded={groupDefaultExpanded}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          noRowsOverlayComponent={TableNoRowOverlay}
          loadingOverlayComponent={TableLoadingOverlay}
          // autoSizeStrategy={{
          //   type: 'fitCellContents',
          //   skipHeader: true,
          // }}
          accentedSort={accentedSort}
          suppressNoRowsOverlay={suppressNoRowsOverlay}
          rowSelection={rowSelection}
          suppressPaginationPanel
          suppressMovableColumns
          suppressCopyRowsToClipboard // suppressCopyRowsToClipboard=true => allow copy just only selected cell, suppressCopyRowsToClipboard=false =>allow copy whole row
          onSortChanged={onSortChanged}
          onColumnResized={onColumnResized}
          onGridReady={onGridReady}
          domLayout={domLayout}
          {...etc}
        />
        {rowData && rowData?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <TablePagination />
          </div>
        )}
      </div>
    </div>
  )
}

export default TableContent
